import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { ScenarioStatus } from "../utils/worklist";

interface ScenarioStatusPillProps {
  status: ScenarioStatus;
}

/**
 * Compact status badge for a worklist row. "Missing" is the operator's primary
 * signal (the thing they came to fix) so it reads in the warning colour;
 * configured rows are de-emphasised with the success colour and read as "done".
 * Colour comes only from the dot + label so we can lean on existing semantic
 * status tokens instead of inventing soft-background hex values.
 */
export function ScenarioStatusPill({ status }: ScenarioStatusPillProps) {
  const { theme } = useUnistyles();
  const isConfigured = status === "configured";
  const color = isConfigured ? theme.colors.status.success : theme.colors.status.warning;
  return <StatusBadge color={color} label={isConfigured ? "Configured" : "Missing"} />;
}

/** Tooltip copy is shared with any callers that want to explain the pills. */
export const STALE_TOOLTIP = "Scenario no longer configured in camera";
export const EXCLUDED_TOOLTIP = "Excluded from road sense report";

/**
 * Extra, independent signals rendered beside the primary Configured/Missing
 * pill — only when true. `stale` reuses the warning token (a soft "attention"
 * signal, like Missing) while `excluded` uses the error token so an operator
 * override reads as clearly distinct from a camera-reported state.
 *
 * Tooltip is a native `title` on web (zero-dep, hover-only). On native the
 * `title` prop is a no-op, which is acceptable — these pills are only rendered
 * on the `.web` lists today.
 */
export function ScenarioStalePill() {
  const { theme } = useUnistyles();
  return <StatusBadge color={theme.colors.status.warning} label="Stale" title={STALE_TOOLTIP} />;
}

export function ScenarioExcludedPill() {
  const { theme } = useUnistyles();
  return <StatusBadge color={theme.colors.status.error} label="Excluded" title={EXCLUDED_TOOLTIP} />;
}

function StatusBadge({ color, label, title }: { color: string; label: string; title?: string }) {
  const { theme } = useUnistyles();
  return (
    <View
      // `title` is a web-only DOM attribute; RN types don't know it but it is
      // forwarded to the underlying <div>, giving a native hover tooltip.
      {...(title !== undefined ? ({ title } as object) : {})}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing.xs,
        paddingVertical: theme.spacing.xxs,
        paddingHorizontal: theme.spacing.md,
        borderRadius: theme.spacing["3xl"],
        backgroundColor: theme.colors.surfaceCardInner,
      }}
    >
      <View
        style={{
          width: theme.spacing.md,
          height: theme.spacing.md,
          borderRadius: theme.spacing.xs,
          backgroundColor: color,
        }}
      />
      <Text textStyle="xs/medium" style={{ color }}>
        {label}
      </Text>
    </View>
  );
}
